import { ref, watch } from 'vue';
import type { Ref } from 'vue';
import { defineStore } from 'pinia';

import ApiGnssVisibility from '@/api/ApiGnssVisibility';
import { getAxiosErrorStatus, logError } from '@/api/ApiHelpers';

import { FEATURE_TYPE } from '@/components/map/consts/mapFeatureConsts';
import type { GnssStatItem } from '@/components/map/types/gnssStatsTypes';
import { isFeatureDataForType } from '@/components/map/utils/mapFeatureGuards';
import { useMapFeaturesStore } from '@/stores/mapFeaturesStore';
import { useSubNetworksStore } from '@/stores/subNetworksStore';

import type {
	GnssVisibilityPerGridpointItemRaw,
	GnssVisibilityPerGridpointResponse,
	GnssVisibilityWithSignalsItemRaw,
	GnssVisibilityWithSignalsResponse,
} from '@/api/ApiTypes';
import type {
	GnssCode,
	GnssTooltipDisplayItem,
	MetricDbItem,
	MetricDbItems,
	Mountpoint,
} from '@/stores/types/mapStoreContracts';


type GnssVisibilitySnapshotItemRaw =
	| GnssVisibilityWithSignalsItemRaw
	| GnssVisibilityPerGridpointItemRaw;

type GnssVisibilityFeatureType = typeof FEATURE_TYPE.baseStation | typeof FEATURE_TYPE.gridpoint;


type GnssVisibilityStoreSetup = {
	dbItems: Ref<MetricDbItems>;
	displayItems: Ref<GnssTooltipDisplayItem[]>;
	nonNullGnss: Ref<GnssCode[]>;
	isVisible: Ref<boolean>;
	refresh: () => void;
	setVisibility: (visibility: boolean) => void;
};


type GnssPerGridpointStoreSetup = GnssVisibilityStoreSetup & {
	showGridpointsOnly: Ref<boolean>;
};


export const storeVisibilityItemInObject = (
	visibilityObject: MetricDbItem,
	item: GnssVisibilitySnapshotItemRaw,
	showSatellitesInSolution: boolean,
): void => {
	const stat: GnssStatItem = {
		valueBright: item.satellites,
		valuePale: showSatellitesInSolution && 'satellites_in_solution' in item
			? item.satellites_in_solution
			: null,
		currentDatetimeBright: item.current_datetime,
		currentDatetimePale: showSatellitesInSolution && 'current_datetime_in_solution' in item
			? (item.current_datetime_in_solution ?? null)
			: null,
		valueIsOld: item.value_is_old,
		minValue: null,
		maxValue: null,
	};
	if (showSatellitesInSolution && 'signals' in item) {
		stat.signals = item.signals;
	}
	if (showSatellitesInSolution && 'rtcm_messages' in item) {
		stat.rtcmMessages = item.rtcm_messages;
	}
	visibilityObject[item.gnss] = stat;
};


const buildGnssVisibilityStore = (
	featureType: GnssVisibilityFeatureType,
	showGridpointsOnly?: Ref<boolean>,
): GnssVisibilityStoreSetup => {
	const dbItems = ref<MetricDbItems>(new Map());
	const displayItems = ref<GnssTooltipDisplayItem[]>([]);
	const nonNullGnss = ref<GnssCode[]>([]);
	const isVisible = ref(false);

	const featuresStore = useMapFeaturesStore();

	const getDisplayItems = (): GnssTooltipDisplayItem[] => {
		if (!isVisible.value) {
			return [];
		}
		const displayItemsResult: GnssTooltipDisplayItem[] = [];
		for (const feature of featuresStore.features) {
			if (feature.featureType !== featureType) {
				continue;
			}
			const featureData = feature.featureData;
			if (!isFeatureDataForType(featureData, featureType)) {
				continue;
			}
			const mountpointRaw = featureData.mntpnt_int;
			const mountpoint = typeof mountpointRaw === 'string' ? mountpointRaw : null;
			displayItemsResult.push({
				featureType,
				featureData,
				coords: feature.coords,
				stats: mountpoint != null && dbItems.value.has(mountpoint)
					? dbItems.value.get(mountpoint) ?? null
					: null,
			});
		}
		return displayItemsResult;
	};

	const updateDisplayItems = (): void => {
		displayItems.value = getDisplayItems();
	};

	watch(() => featuresStore.features, updateDisplayItems);

	const updateDbItems = (dbItemsValue: GnssVisibilitySnapshotItemRaw[]): void => {
		const satelliteCounts = new Map<GnssCode, number>();
		for (const item of dbItemsValue) {
			const prev = satelliteCounts.get(item.gnss) ?? 0;
			satelliteCounts.set(item.gnss, prev + item.satellites);
		}
		const showGnss: GnssCode[] = [];
		for (const [gnss, count] of satelliteCounts) {
			if (count > 0) {
				showGnss.push(gnss);
			}
		}
		const showGnssSet = new Set(showGnss);
		const sortedItems = new Map<Mountpoint, MetricDbItem>();
		const showSatellitesInSolution = featureType === FEATURE_TYPE.baseStation;
		for (const item of dbItemsValue) {
			if (!showGnssSet.has(item.gnss)) {
				continue;
			}
			const bucket = sortedItems.get(item.mountpoint) ?? {};
			sortedItems.set(item.mountpoint, bucket);
			storeVisibilityItemInObject(bucket, item, showSatellitesInSolution);
		}
		dbItems.value = sortedItems;
		nonNullGnss.value = [...new Set(showGnss)];
		updateDisplayItems();
	};

	const handleGnssVisibilityGet = (
		responseData: GnssVisibilityWithSignalsResponse | GnssVisibilityPerGridpointResponse,
	): void => {
		updateDbItems(responseData.items);
	};

	const handleGnssVisibilityGetError = (error: unknown): void => {
		if (getAxiosErrorStatus(error) !== 401) {
			logError(error);
		}
	};

	const subNetworksStore = useSubNetworksStore();

	const refreshBaseStationMode = (selectChanged: boolean): void => {
		if (isVisible.value) {
			if (selectChanged) {
				updateDbItems([]);
			}
			ApiGnssVisibility.gnssVisibilityBaseStationsGet(
				subNetworksStore.subNetworkSelectedId,
				handleGnssVisibilityGet,
				handleGnssVisibilityGetError,
			);
		}
	};

	const refreshGridpointMode = (selectChanged: boolean): void => {
		const gridpointsOnly = showGridpointsOnly?.value ?? false;
		if (isVisible.value && !gridpointsOnly) {
			if (selectChanged) {
				updateDbItems([]);
			}
			if (subNetworksStore.cnetSelectedId == null) {
				updateDbItems([]);
			} else {
				ApiGnssVisibility.gnssVisibilityGridpointsGet(
					subNetworksStore.cnetSelectedId,
					handleGnssVisibilityGet,
					handleGnssVisibilityGetError,
				);
			}
		}
	};

	const refresh = (): void => {
		if (featureType === FEATURE_TYPE.baseStation) {
			refreshBaseStationMode(false);
		} else {
			refreshGridpointMode(false);
		}
	};

	if (featureType === FEATURE_TYPE.baseStation) {
		watch(() => subNetworksStore.subNetworkSelectedId, () => {
			refreshBaseStationMode(true);
		});
	} else {
		watch(() => subNetworksStore.cnetSelectedId, () => {
			refreshGridpointMode(true);
		});
	}

	const setVisibility = (visibility: boolean): void => {
		isVisible.value = visibility;
		updateDisplayItems();
		refresh();
	};

	return {
		dbItems,
		displayItems,
		nonNullGnss,
		isVisible,
		refresh,
		setVisibility,
	};
};


export const useGnssVisibilityStore = defineStore(
	'gnssVisibilityStorebaseStation',
	() => buildGnssVisibilityStore(FEATURE_TYPE.baseStation),
);

export const useGnssPerGridpointStore = defineStore(
	'gnssVisibilityStoregridpoint',
	(): GnssPerGridpointStoreSetup => {
		const showGridpointsOnly = ref(false);
		return {
			...buildGnssVisibilityStore(FEATURE_TYPE.gridpoint, showGridpointsOnly),
			showGridpointsOnly,
		};
	},
);
